import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useSeo, useJsonLd } from "@/lib/seo";

// Very small, safe Markdown → HTML for our seeded editorial content.
// Handles: # H1, ## H2, **bold**, *italic*, [link](url), paragraphs, line breaks.
function renderMarkdown(md) {
  if (!md) return "";
  const esc = (s) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  const lines = md.split("\n");
  const out = [];
  let para = [];
  const flushPara = () => {
    if (!para.length) return;
    const text = para.join(" ").trim();
    if (text) out.push(`<p>${text}</p>`);
    para = [];
  };
  const inlines = (raw) => {
    let t = esc(raw);
    t = t.replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>");
    t = t.replace(/\*([^*]+)\*/g, "<em>$1</em>");
    t = t.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noreferrer">$1</a>');
    return t;
  };
  for (const line of lines) {
    if (line.startsWith("## ")) {
      flushPara();
      out.push(`<h2>${inlines(line.slice(3).trim())}</h2>`);
    } else if (line.startsWith("# ")) {
      flushPara();
      out.push(`<h1>${inlines(line.slice(2).trim())}</h1>`);
    } else if (line.trim() === "") {
      flushPara();
    } else {
      para.push(inlines(line));
    }
  }
  flushPara();
  return out.join("\n");
}

export default function JournalArticle() {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [state, setState] = useState("loading"); // loading | ok | notfound

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/articles/slug/${slug}`);
        if (cancelled) return;
        setArticle(data);
        setState("ok");
      } catch {
        if (!cancelled) setState("notfound");
      }
    })();
    return () => { cancelled = true; };
  }, [slug]);

  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const url = `${origin}/journal/${slug}`;

  useSeo({
    title: article ? `${article.title} · BARIQUE Journal` : "Journal · BARIQUE",
    description: (article?.excerpt || "").slice(0, 160),
    canonical: url,
    image: article?.cover,
    type: "article",
  });

  useJsonLd(article ? [
    {
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      headline: article.title,
      description: article.excerpt,
      image: article.cover ? [article.cover] : undefined,
      author: { "@type": "Organization", name: article.author || "House of Barique" },
      publisher: {
        "@type": "Organization",
        name: "BARIQUE",
        logo: {
          "@type": "ImageObject",
          url: `${origin}/logo.png`,
        },
      },
      datePublished: article.published_at,
      dateModified: article.updated_at || article.published_at,
      mainEntityOfPage: { "@type": "WebPage", "@id": url },
      keywords: (article.tags || []).join(", "),
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: `${origin}/` },
        { "@type": "ListItem", position: 2, name: "Journal", item: `${origin}/journal` },
        { "@type": "ListItem", position: 3, name: article.title, item: url },
      ],
    },
  ] : []);

  if (state === "loading") {
    return <div className="pt-32 container-lux min-h-[60vh]"><p className="overline text-muted-foreground/60">Loading…</p></div>;
  }
  if (state === "notfound" || !article) {
    return (
      <div className="pt-32 container-lux min-h-[60vh]">
        <h1 className="font-serif text-4xl">Story not found</h1>
        <Link to="/journal" className="lux-link mt-6 inline-block">Back to Journal</Link>
      </div>
    );
  }

  return (
    <article className="pt-28 pb-24 bg-ivory" data-testid="journal-article">
      {article.cover && (
        <div className="w-full h-[60vh] md:h-[75vh] overflow-hidden bg-muted">
          <img src={article.cover} alt={article.title} className="w-full h-full object-cover" />
        </div>
      )}
      <div className="container-lux max-w-3xl">
        <nav className="overline text-muted-foreground mt-10 mb-6" aria-label="Breadcrumb">
          <Link to="/">Home</Link> · <Link to="/journal">Journal</Link> · <span className="text-ink">{article.title}</span>
        </nav>
        <p className="overline text-muted-foreground">
          {(article.tags || []).slice(0, 3).join(" · ")}
          {article.read_minutes ? ` · ${article.read_minutes} min read` : ""}
        </p>
        <h1 className="mt-6 font-serif tracking-tighter text-4xl md:text-6xl font-light leading-[1.05]">
          {article.title}
        </h1>
        {article.excerpt && (
          <p className="mt-8 text-lg md:text-xl text-muted-foreground leading-relaxed">{article.excerpt}</p>
        )}
        <div className="divider-thin my-12" />
        <div
          className="journal-prose text-base md:text-lg leading-relaxed text-ink/85"
          dangerouslySetInnerHTML={{ __html: renderMarkdown(article.body_md) }}
        />
        <div className="mt-16 pt-10 border-t border-border">
          <p className="overline text-muted-foreground">Written by</p>
          <p className="mt-2 font-serif text-xl">{article.author || "House of Barique"}</p>
          <Link to="/journal" className="lux-link mt-10 inline-block">← Back to Journal</Link>
        </div>
      </div>
    </article>
  );
}
